import React, { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineBathIcon from 'LuxKit/Icons/line/LineBathIcon'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import Modal from 'LuxKit/Modal/Modal'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import TextLink from 'LuxKit/TextLink'
import VillasPageAmenitiesModalContent from 'components/OfferPage/Villas/VillasPageAmenitiesModalContent'
import ModalContext from 'contexts/ModalContext'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import { getMaxGuestCount } from 'lib/offer/occupancyUtils'
import { rem } from 'polished'
import styled from 'styled-components'
import { EmptyArray, take } from 'lib/array/arrayUtils'

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: 100%;
  gap: ${rem(12)};
  padding: ${rem(8)} 0;
`

interface Props {
  offer: App.VillaOffer | App.VillaOfferSummary
  offerUrl: string
  totalInclusions?: number
}

function VillaCardInclusions(props: Props) {
  const {
    offer,
    offerUrl,
    totalInclusions = 4,
  } = props

  const intl = useIntl()

  const amenities: Array<App.PackageRoomAmenity> = offer.lowestPricePackage?.roomType.amenities ?? EmptyArray
  const numberOfBedrooms = offer.property.bedrooms
  const numberOfBathrooms = offer.property.bathrooms
  const capacity = useMemo((): number => getMaxGuestCount(offer.lowestPricePackage?.roomRate?.capacities), [offer.lowestPricePackage?.roomRate?.capacities])

  const showModal = useContext(ModalContext)
  const openAmenitiesModal = useCallback((e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault()
    showModal(
      <Modal
        title={intl.formatMessage({
          id: 'BlgomjmZzh',
          defaultMessage: 'Amenities',
          description: 'Title for villa amenities modal',
        })}
        primaryActionTo={offerUrl}
        primaryActionText={intl.formatMessage({
          id: 'Bmpg6g4qA5',
          defaultMessage: 'View offer',
          description: 'Primary action button text in villa amenities modal',
        })}
      >
        <VillasPageAmenitiesModalContent amenities={amenities} />
      </Modal>,
    )
  }, [showModal, offerUrl, amenities, intl])

  // numberOfBedrooms, numberOfBathrooms and capacity are already shown, so we want the other amenities
  // NOTE: We're taking only 3 amenities because we show at most 6 on hero tile
  const otherAmenities = useMemo(() => take(amenities, totalInclusions - 3), [totalInclusions, amenities])

  return (<VerticalSpacer gap={8}>
    <GridContainer>
      {!!capacity && <BodyTextBlock variant="medium" colour="neutral-two" startIcon={<LineUsersAltIcon />}>
        {intl.formatMessage(
          {
            id: 'NVCHuz0qao',
            defaultMessage: '{count, plural, one {# guest} other {# guests}}',
            description: 'Guest capacity count on villa search card',
          },
          { count: capacity },
        )}
      </BodyTextBlock>}
      {!!numberOfBedrooms && <BodyTextBlock variant="medium" colour="neutral-two" startIcon={<LineBedIcon />}>
        {intl.formatMessage(
          {
            id: 'vc4v9nOY40',
            defaultMessage: '{count, plural, one {# bedroom} other {# bedrooms}}',
            description: 'Bedroom count on villa search card',
          },
          { count: numberOfBedrooms },
        )}
      </BodyTextBlock>}
      {!!numberOfBathrooms && <BodyTextBlock variant="medium" colour="neutral-two" startIcon={<LineBathIcon />}>
        {intl.formatMessage(
          {
            id: '3C7ZJfQ3lM',
            defaultMessage: '{count, plural, one {# bathroom} other {# bathrooms}}',
            description: 'Bathroom count on villa search card',
          },
          { count: numberOfBathrooms },
        )}
      </BodyTextBlock>}
      {otherAmenities.map((amenity) => <OfferInclusionItem
        key={amenity.id}
        inclusion={amenity}
      />)}
    </GridContainer>
    {amenities.length > 1 && <TextLink size="medium" onClick={openAmenitiesModal}>
      {intl.formatMessage(
        {
          id: 'Tq5CTMGF0J',
          defaultMessage: 'Show all {count} amenities',
          description: 'Link text to show all villa amenities in a modal',
        },
        { count: amenities.length },
      )}
    </TextLink>}
  </VerticalSpacer>)
}

export default VillaCardInclusions
